import {
  ExcludedVendorRecord,
  ConsideredVendorRecord,
  ProductWithCodes,
  ProductWithVendors,
  VendorCandidateAllocation,
  VendorCandidateSourceCount,
  VendorCandidateSummary,
  RecommendedVendorFeedback,
  VendorAwardEvidence,
  VendorRecord,
  VendorWarning,
} from '../types/pipeline';

const toNonNegativeInteger = (value: unknown): number | undefined => {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : undefined;
};

const firstDefined = (...values: unknown[]): unknown => values.find((value) => value !== undefined);

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter((item): item is string => item.length > 0);
};

const uniqueStrings = (values: string[]): string[] => {
  return Array.from(new Set(values.filter((val): val is string => typeof val === 'string' && val.length > 0)));
};

const toStringArrayOrString = (value: unknown): string[] => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  return toStringArray(value);
};

const toTrimmedString = (value: unknown): string | undefined => {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
};

const toFiniteNumber = (value: unknown): number | undefined => {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

const normalizeAwardEvidence = (value: unknown): VendorAwardEvidence[] => {
  const entries = Array.isArray(value) ? value : value && typeof value === 'object' ? [value] : [];
  return entries.reduce<VendorAwardEvidence[]>((evidence, entry) => {
    if (!entry || typeof entry !== 'object') return evidence;
    const raw = entry as Record<string, unknown>;
    evidence.push({
      ...(toFiniteNumber(raw.amount) !== undefined ? { amount: toFiniteNumber(raw.amount) } : {}),
      ...(toTrimmedString(raw.date) ? { date: toTrimmedString(raw.date) } : {}),
      ...(toTrimmedString(raw.naics) ? { naics: toTrimmedString(raw.naics) } : {}),
      ...(toTrimmedString(raw.psc) ? { psc: toTrimmedString(raw.psc) } : {}),
      ...(toTrimmedString(raw.description) ? { description: toTrimmedString(raw.description) } : {}),
      ...(toTrimmedString(firstDefined(raw.award_id, raw.awardId))
        ? { awardId: toTrimmedString(firstDefined(raw.award_id, raw.awardId)) }
        : {}),
      ...(toTrimmedString(firstDefined(raw.award_url, raw.awardUrl))
        ? { awardUrl: toTrimmedString(firstDefined(raw.award_url, raw.awardUrl)) }
        : {}),
      ...(toTrimmedString(firstDefined(raw.description_excerpt, raw.descriptionExcerpt))
        ? { descriptionExcerpt: toTrimmedString(firstDefined(raw.description_excerpt, raw.descriptionExcerpt)) }
        : {}),
      ...(toStringArray(firstDefined(raw.matched_terms, raw.matchedSalientPhrases)).length > 0
        ? { matchedSalientPhrases: toStringArray(firstDefined(raw.matched_terms, raw.matchedSalientPhrases)) }
        : {}),
      ...(toStringArray(firstDefined(raw.matched_concepts, raw.matchedSalientConcepts)).length > 0
        ? { matchedSalientConcepts: toStringArray(firstDefined(raw.matched_concepts, raw.matchedSalientConcepts)) }
        : {}),
      ...(toTrimmedString(firstDefined(raw.match_method, raw.matchMethod))
        ? { matchMethod: toTrimmedString(firstDefined(raw.match_method, raw.matchMethod)) }
        : {}),
      ...(toTrimmedString(firstDefined(raw.code_match, raw.codeMatch))
        ? { codeMatch: toTrimmedString(firstDefined(raw.code_match, raw.codeMatch)) }
        : {}),
      ...(toTrimmedString(firstDefined(raw.description_state, raw.descriptionState))
        ? { descriptionState: toTrimmedString(firstDefined(raw.description_state, raw.descriptionState)) }
        : {}),
      ...(toFiniteNumber(firstDefined(raw.semantic_score, raw.semanticScore)) !== undefined
        ? { semanticScore: toFiniteNumber(firstDefined(raw.semantic_score, raw.semanticScore)) }
        : {}),
      ...(typeof firstDefined(raw.semantic_score_available, raw.semanticScoreAvailable) === 'boolean'
        ? { semanticScoreAvailable: firstDefined(raw.semantic_score_available, raw.semanticScoreAvailable) as boolean }
        : {}),
      ...(toTrimmedString(firstDefined(raw.semantic_model_version, raw.semanticModelVersion))
        ? { semanticModelVersion: toTrimmedString(firstDefined(raw.semantic_model_version, raw.semanticModelVersion)) }
        : {}),
      ...(toTrimmedString(firstDefined(raw.llm_evidence_verdict, raw.llmEvidenceVerdict))
        ? { llmEvidenceVerdict: toTrimmedString(firstDefined(raw.llm_evidence_verdict, raw.llmEvidenceVerdict)) }
        : {}),
      ...(toTrimmedString(firstDefined(raw.llm_evidence_excerpt, raw.llmEvidenceExcerpt))
        ? { llmEvidenceExcerpt: toTrimmedString(firstDefined(raw.llm_evidence_excerpt, raw.llmEvidenceExcerpt)) }
        : {}),
      ...(typeof firstDefined(raw.llm_evidence_fallback, raw.llmEvidenceFallback) === 'boolean'
        ? { llmEvidenceFallback: firstDefined(raw.llm_evidence_fallback, raw.llmEvidenceFallback) as boolean }
        : {}),
    });
    return evidence;
  }, []);
};

const normalizeVendorWarnings = (value: unknown): VendorWarning[] => {
  const entries = Array.isArray(value) ? value : value == null ? [] : [value];
  return entries.reduce<VendorWarning[]>((warnings, entry) => {
    if (typeof entry === 'string' && entry.trim()) {
      warnings.push({ message: entry.trim() });
      return warnings;
    }
    if (!entry || typeof entry !== 'object') return warnings;
    const raw = entry as Record<string, unknown>;
    if (typeof raw.message !== 'string' || !raw.message.trim()) return warnings;
    warnings.push({
      ...(typeof raw.code === 'string' && raw.code.trim() ? { code: raw.code.trim() } : {}),
      message: raw.message.trim(),
    });
    return warnings;
  }, []);
};

const coerceName = (record: any, fallback: string): string => {
  if (record && typeof record.name === 'string' && record.name.trim().length > 0) {
    return record.name.trim();
  }
  if (typeof record === 'string' && record.trim().length > 0) {
    return record.trim();
  }
  return fallback;
};

export const normalizeVendorRecord = (record: any): VendorRecord => {
  const name = coerceName(record, 'Unknown Vendor');
  const businessTypes = toStringArray(record?.business_types);

  return {
    name,
    cage_code: typeof record?.cage_code === 'string' ? record.cage_code : null,
    business_types: uniqueStrings(businessTypes),
    is_active: typeof record?.is_active === 'boolean' ? record.is_active : null,
    is_small_business: typeof record?.is_small_business === 'boolean' ? record.is_small_business : null,
    smallBusinessPriorityState: [
      'verified_small_for_matching_naics',
      'verified_not_small_for_matching_naics',
      'size_unknown',
    ].includes(record?.small_business_priority_state ?? record?.smallBusinessPriorityState)
      ? (record.small_business_priority_state ?? record.smallBusinessPriorityState)
      : undefined,
    matchingNaicsCodes: uniqueStrings(toStringArrayOrString(
      record?.matching_naics_codes ?? record?.matchingNaicsCodes,
    )),
    occupiedSmallBusinessReservedSlot: (record?.occupied_small_business_reserved_slot
      ?? record?.occupiedSmallBusinessReservedSlot) === true,
    total_amount:
      typeof record?.total_amount === 'number' && !Number.isNaN(record.total_amount)
        ? record.total_amount
        : undefined,
    uei: typeof record?.uei === 'string' && record.uei.trim().length > 0 ? record.uei.trim() : null,
    contact_info: record?.contact_info ? {
      name: record.contact_info.name || undefined,
      emails: Array.isArray(record.contact_info.emails) ? record.contact_info.emails : [],
      phones: Array.isArray(record.contact_info.phones) ? record.contact_info.phones : [],
      source: record.contact_info.source || 'not_found',
      extracted_from: record.contact_info.extracted_from || undefined,
    } : null,
    capabilities: toTrimmedString(record?.capabilities),
    past_performance: toTrimmedString(record?.past_performance),
    website: typeof record?.website === 'string' && record.website.trim().length > 0 ? record.website.trim() : null,
    sourceProvenance: uniqueStrings(toStringArrayOrString(record?.source_provenance ?? record?.sourceProvenance)),
    selectedCodeMatch: typeof (record?.selected_code_match ?? record?.selectedCodeMatch) === 'boolean'
      ? (record.selected_code_match ?? record.selectedCodeMatch)
      : null,
    awardEvidence: normalizeAwardEvidence(record?.award_evidence ?? record?.awardEvidence),
    relevanceRationale: typeof (record?.relevance_rationale ?? record?.relevanceRationale) === 'string'
      && (record.relevance_rationale ?? record.relevanceRationale).trim().length > 0
      ? (record.relevance_rationale ?? record.relevanceRationale).trim()
      : null,
    warnings: normalizeVendorWarnings(record?.warnings),
    registrationOnly: (record?.registration_only ?? record?.registrationOnly) === true,
  };
};

const normalizeExcludedVendorRecords = (value: unknown): ExcludedVendorRecord[] => {
  if (!Array.isArray(value)) return [];

  return value.reduce<ExcludedVendorRecord[]>((records, record) => {
    if (!record || typeof record !== 'object') return records;

    const name = typeof (record as any).name === 'string' ? (record as any).name.trim() : '';
    const reasonCode = typeof (record as any).reason_code === 'string'
      ? (record as any).reason_code.trim()
      : '';
    const reason = typeof (record as any).reason === 'string' ? (record as any).reason.trim() : '';
    if (!name || !reasonCode || !reason) return records;

    const rawCage = (record as any).cage ?? (record as any).cage_code;
    records.push({
      name,
      reason_code: reasonCode,
      reason,
      cage: typeof rawCage === 'string' && rawCage.trim().length > 0 ? rawCage.trim() : null,
      uei: typeof (record as any).uei === 'string' && (record as any).uei.trim().length > 0
        ? (record as any).uei.trim()
        : null,
      is_active: typeof (record as any).is_active === 'boolean' ? (record as any).is_active : null,
      is_small_business: typeof (record as any).is_small_business === 'boolean'
        ? (record as any).is_small_business
        : null,
    });
    return records;
  }, []);
};

const normalizeConsideredVendorRecords = (value: unknown): ConsideredVendorRecord[] => {
  if (!Array.isArray(value)) return [];
  return value.reduce<ConsideredVendorRecord[]>((records, entry) => {
    if (!entry || typeof entry !== 'object') return records;
    const raw = entry as Record<string, any>;
    const stableKey = toTrimmedString(raw.stable_key ?? raw.stableKey);
    const schemaVersion = toTrimmedString(raw.schema_version ?? raw.schemaVersion);
    const vendor = raw.vendor && typeof raw.vendor === 'object' ? normalizeVendorRecord(raw.vendor) : null;
    const selection = raw.selection && typeof raw.selection === 'object' ? raw.selection : null;
    const reasonCode = toTrimmedString(selection?.reason_code ?? selection?.reasonCode);
    const reason = toTrimmedString(selection?.reason);
    if (!stableKey || !schemaVersion || !vendor || !selection || !reasonCode || !reason) return records;
    const original = raw.original_selection ?? raw.originalSelection;
    const audit = raw.audit && typeof raw.audit === 'object' ? raw.audit : null;
    records.push({
      schemaVersion,
      stableKey,
      vendor,
      selection: {
        selectable: selection.selectable === true,
        reasonCode,
        reason,
      },
      ...(original && typeof original === 'object' && toTrimmedString(original.state)
        && toTrimmedString(original.reason_code ?? original.reasonCode)
        && toTrimmedString(original.reason)
        ? { originalSelection: {
            state: toTrimmedString(original.state)!,
            reasonCode: toTrimmedString(original.reason_code ?? original.reasonCode)!,
            reason: toTrimmedString(original.reason)!,
          } }
        : {}),
      ...(audit ? { audit: {
        ...(toStringArray(audit.retrieval_basis ?? audit.retrievalBasis).length > 0
          ? { retrievalBasis: toStringArray(audit.retrieval_basis ?? audit.retrievalBasis) } : {}),
        ...(toStringArray(audit.source_provenance ?? audit.sourceProvenance).length > 0
          ? { sourceProvenance: toStringArray(audit.source_provenance ?? audit.sourceProvenance) } : {}),
        ...(toStringArray(audit.award_ids ?? audit.awardIds).length > 0
          ? { awardIds: toStringArray(audit.award_ids ?? audit.awardIds) } : {}),
      } } : {}),
    });
    return records;
  }, []);
};

const normalizeRecommendedVendorFeedback = (value: unknown): RecommendedVendorFeedback[] => {
  if (!Array.isArray(value)) return [];

  return value.reduce<RecommendedVendorFeedback[]>((feedback, entry) => {
    if (!entry || typeof entry !== 'object') return feedback;
    const raw = entry as Record<string, any>;
    const recommendation = raw.recommendation && typeof raw.recommendation === 'object'
      ? raw.recommendation
      : {};
    const resolution = raw.resolution === 'resolved' || raw.resolution === 'input_error'
      ? raw.resolution
      : null;
    if (!resolution) return feedback;

    const error = normalizeVendorWarnings(raw.error)[0] ?? null;
    feedback.push({
      recommendation: {
        ...(typeof recommendation.name === 'string' && recommendation.name.trim()
          ? { name: recommendation.name.trim() }
          : {}),
        ...(typeof recommendation.uei === 'string' && recommendation.uei.trim()
          ? { uei: recommendation.uei.trim() }
          : {}),
        ...(typeof recommendation.cage === 'string' && recommendation.cage.trim()
          ? { cage: recommendation.cage.trim() }
          : {}),
      },
      selected_naics: typeof raw.selected_naics === 'string' && raw.selected_naics.trim()
        ? raw.selected_naics.trim()
        : null,
      resolution,
      ...(raw.candidate && typeof raw.candidate === 'object'
        ? { candidate: normalizeVendorRecord(raw.candidate) }
        : {}),
      ...(error ? { error } : {}),
    });
    return feedback;
  }, []);
};

const normalizeSourceCount = (value: unknown): VendorCandidateSourceCount | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  const count: VendorCandidateSourceCount = {
    ...(toNonNegativeInteger(raw.retrieved) !== undefined ? { retrieved: toNonNegativeInteger(raw.retrieved) } : {}),
    ...(toNonNegativeInteger(raw.eligible) !== undefined ? { eligible: toNonNegativeInteger(raw.eligible) } : {}),
    ...(toNonNegativeInteger(raw.pooled) !== undefined ? { pooled: toNonNegativeInteger(raw.pooled) } : {}),
    ...(toNonNegativeInteger(firstDefined(raw.enriched_qualified, raw.enrichedQualified)) !== undefined
      ? { enrichedQualified: toNonNegativeInteger(firstDefined(raw.enriched_qualified, raw.enrichedQualified)) }
      : {}),
    ...(toNonNegativeInteger(raw.displayed) !== undefined ? { displayed: toNonNegativeInteger(raw.displayed) } : {}),
  };
  return Object.keys(count).length > 0 ? count : undefined;
};

const normalizeCandidateSummary = (value: unknown): VendorCandidateSummary | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  const rawSourceCounts = firstDefined(raw.source_counts, raw.sourceCounts);
  const sourceCountsRecord = rawSourceCounts && typeof rawSourceCounts === 'object'
    ? rawSourceCounts as Record<string, unknown>
    : null;
  const usaspendingAward = normalizeSourceCount(sourceCountsRecord && firstDefined(
    sourceCountsRecord.usaspending_award,
    sourceCountsRecord.usaspendingAward,
  ));
  const samRegistrationMatch = normalizeSourceCount(sourceCountsRecord && firstDefined(
    sourceCountsRecord.sam_registration_match,
    sourceCountsRecord.samRegistrationMatch,
  ));
  const crossCodeUsaspendingAward = normalizeSourceCount(sourceCountsRecord && firstDefined(
    sourceCountsRecord.cross_code_usaspending_award,
    sourceCountsRecord.crossCodeUsaspendingAward,
    sourceCountsRecord.cross_code_award,
    sourceCountsRecord.crossCodeAward,
  ));
  if (!usaspendingAward && !crossCodeUsaspendingAward && !samRegistrationMatch) return undefined;

  const rawAllocation = firstDefined(raw.allocation);
  if (!rawAllocation || typeof rawAllocation !== 'object') return undefined;
  const allocationRecord = rawAllocation as Record<string, unknown>;
  const rawSelectedByBucket = firstDefined(
    allocationRecord.selected_by_bucket,
    allocationRecord.selectedByBucket,
  );
  if (!rawSelectedByBucket || typeof rawSelectedByBucket !== 'object') return undefined;
  const selectedByBucket = rawSelectedByBucket as Record<string, unknown>;
  const finalNonManufacturerSlots = toNonNegativeInteger(firstDefined(
    allocationRecord.final_non_manufacturer_slots,
    allocationRecord.finalNonManufacturerSlots,
  ));
  const awardReserved = toNonNegativeInteger(firstDefined(selectedByBucket.award_reserved, selectedByBucket.awardReserved));
  const samReserved = toNonNegativeInteger(firstDefined(selectedByBucket.sam_reserved, selectedByBucket.samReserved));
  const crossCodeAward = toNonNegativeInteger(firstDefined(
    selectedByBucket.cross_code_award,
    selectedByBucket.crossCodeAward,
  )) ?? 0;
  const generalFill = toNonNegativeInteger(firstDefined(selectedByBucket.general_fill, selectedByBucket.generalFill)) ?? 0;
  if (
    finalNonManufacturerSlots === undefined
    || awardReserved === undefined
    || samReserved === undefined
    || awardReserved + samReserved + crossCodeAward + generalFill !== finalNonManufacturerSlots
  ) return undefined;

  const rawReservedSlots = firstDefined(allocationRecord.reserved_slots, allocationRecord.reservedSlots);
  const reservedSlotsRecord = rawReservedSlots && typeof rawReservedSlots === 'object'
    ? rawReservedSlots as Record<string, unknown>
    : null;
  const allocation: VendorCandidateAllocation = {
    ...(toNonNegativeInteger(firstDefined(allocationRecord.manufacturer_slots, allocationRecord.manufacturerSlots)) !== undefined
      ? { manufacturerSlots: toNonNegativeInteger(firstDefined(allocationRecord.manufacturer_slots, allocationRecord.manufacturerSlots)) }
      : {}),
    finalNonManufacturerSlots,
    ...(reservedSlotsRecord ? {
      reservedSlots: {
        ...(toNonNegativeInteger(firstDefined(reservedSlotsRecord.usaspending_award, reservedSlotsRecord.usaspendingAward)) !== undefined
          ? { usaspendingAward: toNonNegativeInteger(firstDefined(reservedSlotsRecord.usaspending_award, reservedSlotsRecord.usaspendingAward)) }
          : {}),
        ...(toNonNegativeInteger(firstDefined(
          reservedSlotsRecord.cross_code_usaspending_award,
          reservedSlotsRecord.crossCodeUsaspendingAward,
          reservedSlotsRecord.cross_code_award,
          reservedSlotsRecord.crossCodeAward,
        )) !== undefined
          ? { crossCodeUsaspendingAward: toNonNegativeInteger(firstDefined(
            reservedSlotsRecord.cross_code_usaspending_award,
            reservedSlotsRecord.crossCodeUsaspendingAward,
            reservedSlotsRecord.cross_code_award,
            reservedSlotsRecord.crossCodeAward,
          )) }
          : {}),
        ...(toNonNegativeInteger(firstDefined(reservedSlotsRecord.sam_registration_match, reservedSlotsRecord.samRegistrationMatch)) !== undefined
          ? { samRegistrationMatch: toNonNegativeInteger(firstDefined(reservedSlotsRecord.sam_registration_match, reservedSlotsRecord.samRegistrationMatch)) }
          : {}),
      },
    } : {}),
    selectedByBucket: { awardReserved, samReserved, ...(crossCodeAward > 0 ? { crossCodeAward } : {}), generalFill },
    ...(firstDefined(allocationRecord.selected_by_allocation_class, allocationRecord.selectedByAllocationClass)
      && typeof firstDefined(allocationRecord.selected_by_allocation_class, allocationRecord.selectedByAllocationClass) === 'object'
      ? (() => {
        const allocationClass = firstDefined(
          allocationRecord.selected_by_allocation_class,
          allocationRecord.selectedByAllocationClass,
        ) as Record<string, unknown>;
        const awardBacked = toNonNegativeInteger(firstDefined(allocationClass.award_backed, allocationClass.awardBacked));
        const selectedCodeAward = toNonNegativeInteger(firstDefined(
          allocationClass.selected_code_award,
          allocationClass.selectedCodeAward,
        ));
        const crossCodeAward = toNonNegativeInteger(firstDefined(
          allocationClass.cross_code_award,
          allocationClass.crossCodeAward,
        ));
        const samOnly = toNonNegativeInteger(firstDefined(allocationClass.sam_only, allocationClass.samOnly));
        if (
          selectedCodeAward !== undefined
          && crossCodeAward !== undefined
          && samOnly !== undefined
          && selectedCodeAward + crossCodeAward + samOnly === finalNonManufacturerSlots
        ) {
          return { selectedByAllocationClass: { selectedCodeAward, crossCodeAward, samOnly } };
        }
        return awardBacked !== undefined && samOnly !== undefined && awardBacked + samOnly === finalNonManufacturerSlots
          ? { selectedByAllocationClass: { awardBacked, samOnly } }
          : {};
      })()
      : {}),
    ...(Array.isArray(firstDefined(allocationRecord.shortfall_slots, allocationRecord.shortfallSlots))
      ? { shortfallSlots: firstDefined(allocationRecord.shortfall_slots, allocationRecord.shortfallSlots) as unknown[] }
      : {}),
    ...(Array.isArray(firstDefined(allocationRecord.redistributed_slots, allocationRecord.redistributedSlots))
      ? { redistributedSlots: firstDefined(allocationRecord.redistributed_slots, allocationRecord.redistributedSlots) as unknown[] }
      : {}),
  };

  return {
    ...(toNonNegativeInteger(firstDefined(raw.qualified_candidate_count, raw.qualifiedCandidateCount)) !== undefined
      ? { qualifiedCandidateCount: toNonNegativeInteger(firstDefined(raw.qualified_candidate_count, raw.qualifiedCandidateCount)) }
      : {}),
    ...(toNonNegativeInteger(firstDefined(raw.excluded_candidate_count, raw.excludedCandidateCount)) !== undefined
      ? { excludedCandidateCount: toNonNegativeInteger(firstDefined(raw.excluded_candidate_count, raw.excludedCandidateCount)) }
      : {}),
    sourceCounts: {
      ...(usaspendingAward ? { usaspendingAward } : {}),
      ...(crossCodeUsaspendingAward ? { crossCodeUsaspendingAward } : {}),
      ...(samRegistrationMatch ? { samRegistrationMatch } : {}),
    },
    allocation,
  };
};

const buildVendorDetailsFromNames = (names: string[]): VendorRecord[] => {
  return uniqueStrings(names).map((name) =>
    normalizeVendorRecord({
      name,
      business_types: [],
      cage_code: null,
      is_active: null,
      is_small_business: null,
      total_amount: undefined,
      uei: null,
    }),
  );
};

const ensureProductId = (product: any, index: number): string => {
  if (typeof product?.id === 'string' && product.id.trim().length > 0) {
    return product.id;
  }
  if (typeof product?.product_id === 'string' && product.product_id.trim().length > 0) {
    return product.product_id;
  }

  const productName =
    typeof product?.product_name === 'string' && product.product_name.trim().length > 0
      ? product.product_name.trim()
      : 'product';
  const manufacturer =
    typeof product?.manufacturer === 'string' && product.manufacturer.trim().length > 0
      ? product.manufacturer.trim()
      : 'vendor';

  const fallback = `${productName}-${manufacturer}-${index}`;
  return fallback.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || `vendor-${index}`;
};

export const normalizeProductsWithVendors = (rawProducts: any[]): ProductWithVendors[] => {
  const productsArray = Array.isArray(rawProducts) ? rawProducts : [];

  return productsArray.map((product: any, index: number) => {
    const vendorRecordsSource =
      Array.isArray(product?.vendor_records) && product.vendor_records.length > 0
        ? product.vendor_records
        : Array.isArray(product?.vendorRecords) && product.vendorRecords.length > 0
          ? product.vendorRecords
          : Array.isArray(product?.vendorDetails) && product.vendorDetails.length > 0
            ? product.vendorDetails
            : [];

    let vendorDetails: VendorRecord[] = vendorRecordsSource.map((record: any) => normalizeVendorRecord(record));

    const vendorNameStrings = toStringArray(product?.vendors);
    if (vendorDetails.length === 0 && vendorNameStrings.length > 0) {
      vendorDetails = buildVendorDetailsFromNames(vendorNameStrings);
    }

    const vendorNames =
      vendorNameStrings.length > 0
        ? uniqueStrings(vendorNameStrings)
        : vendorDetails.map((detail) => detail.name).filter((name) => name.length > 0);

    const id = ensureProductId(product, index);
    const excludedVendorRecords = normalizeExcludedVendorRecords(
      Array.isArray(product?.excluded_vendor_records)
        ? product.excluded_vendor_records
        : product?.excludedVendorRecords,
    );
    const consideredVendorRecords = normalizeConsideredVendorRecords(
      Array.isArray(product?.considered_vendor_records)
        ? product.considered_vendor_records
        : product?.consideredVendorRecords,
    );
    const selectedAddedVendorRecords = Array.isArray(product?.selected_added_vendor_records)
      ? product.selected_added_vendor_records.map((record: any) => normalizeVendorRecord(record))
      : Array.isArray(product?.selectedAddedVendorRecords)
        ? product.selectedAddedVendorRecords.map((record: any) => normalizeVendorRecord(record))
        : [];
    const hasExplicitSelectedVendorRecords = Array.isArray(product?.selected_vendor_records)
      || Array.isArray(product?.selectedVendorRecords);
    const selectedVendorRecords = Array.isArray(product?.selected_vendor_records)
      ? product.selected_vendor_records.map((record: any) => normalizeVendorRecord(record))
      : Array.isArray(product?.selectedVendorRecords)
        ? product.selectedVendorRecords.map((record: any) => normalizeVendorRecord(record))
        : [];
    const recommendedVendorFeedback = normalizeRecommendedVendorFeedback(
      Array.isArray(product?.recommended_vendor_feedback)
        ? product.recommended_vendor_feedback
        : product?.recommendedVendorFeedback,
    );
    const candidateSummary = normalizeCandidateSummary(
      firstDefined(product?.candidate_summary, product?.candidateSummary),
    );

    const normalizedProduct: ProductWithVendors = {
      ...(product as ProductWithCodes),
      id,
      vendors: uniqueStrings(vendorNames),
      vendorDetails,
      vendorRecords: vendorDetails,
      excludedVendorRecords,
      consideredVendorRecords,
      selectedAddedVendorRecords,
      // Legacy outputs encoded a selected considered candidate only through
      // selected_added_vendor_records. Preserve that restore behavior until a
      // Stage-6 output has an explicit selection snapshot.
      selectedVendorRecords: hasExplicitSelectedVendorRecords
        ? selectedVendorRecords
        : selectedAddedVendorRecords,
      recommendedVendorFeedback,
      ...(candidateSummary ? { candidateSummary } : {}),
      ...(toTrimmedString(firstDefined(product?.requirement_context_source, product?.requirementContextSource))
        ? { requirementContextSource: toTrimmedString(firstDefined(product?.requirement_context_source, product?.requirementContextSource)) }
        : {}),
      ...(toTrimmedString(firstDefined(product?.requirement_context_status, product?.requirementContextStatus))
        ? { requirementContextStatus: toTrimmedString(firstDefined(product?.requirement_context_status, product?.requirementContextStatus)) }
        : {}),
    };

    return normalizedProduct;
  });
};
